import type { Maybe } from '@custom/dx3';
import classNames from 'classnames';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import RelativeTime from 'dayjs/plugin/relativeTime';
import {
  LimitedIcon,
  TimerOneThird,
  TimerThreeQuarters,
} from '../../assets/svgs';
import type { Scalars } from '../../models/__generated__/graphql';
import type { Reward } from '../../store/queries/fragments/mission/missionSubFragments.js';
import { isValidDate } from '../../utils/validators';

import missionRewardStyles from '../MissionReward/MissionReward.module.scss';

dayjs.extend(RelativeTime);
dayjs.extend(LocalizedFormat);

// TODO: fix this with the same issue in the MissionCard.tsx
// Graphql is not successfully converting the type passed from the the API into a dayjs
export function ISODateToDate(
  ISODate: Maybe<Scalars['ISODate']['input']> | null | Date
) {
  if (!ISODate) return null;
  const date = new Date(ISODate as unknown as string);
  if (isValidDate(date)) return dayjs(date);
  return null;
}

export const getLimitedClasses = (reward: Reward) => {
  const { outOfStock, limitAmount } = reward;

  const expires = ISODateToDate(reward.expiresAt);

  const now = dayjs();
  if (outOfStock) {
    return classNames('out-of-stock', missionRewardStyles['out-of-stock']);
  } else if (expires && expires.isBefore(now)) {
    return classNames('expired', missionRewardStyles['expired']);
  } else if (limitAmount) {
    return classNames('limited', missionRewardStyles['limited']);
  } else if (expires) {
    if (expires.diff(now, 'days') <= 7) {
      return classNames('expires-soon', missionRewardStyles['expires-soon']);
    }
    return classNames('expires', missionRewardStyles['expires']);
  }
};

export function LimitedInformation({
  reward,
}: {
  reward: Reward;
}): JSX.Element {
  const { outOfStock, limitAmount } = reward;

  const expires = ISODateToDate(reward.expiresAt);

  const now = dayjs();
  if (outOfStock) {
    // I cannot produce this case. Nitro API doesn't tell me about out-of-stock rewards
    return (
      <span
        style={{
          alignItems: 'center',
          display: 'inline-flex',
          flexDirection: 'row',
          gap: '1em',
          justifyContent: 'center',
        }}
      >
        Out of Stock
      </span>
    );
  } else if (expires && expires.isBefore(now)) {
    // I cannot produce this case. Nitro API doesn't tell me about expired rewards
    return (
      <span
        style={{
          alignItems: 'center',
          display: 'inline-flex',
          flexDirection: 'row',
          gap: '1em',
          justifyContent: 'center',
        }}
      >
        Expired
      </span>
    );
  } else if (limitAmount) {
    return (
      <span
        style={{
          alignItems: 'center',
          display: 'inline-flex',
          flexDirection: 'row',
          fontSize: '10px',
          gap: '1em',
          justifyContent: 'center',
        }}
      >
        <LimitedIcon />
        Limited
      </span>
    );
  } else if (expires) {
    const icon =
      expires.diff(now, 'days') <= 7 ? (
        <TimerOneThird />
      ) : (
        <TimerThreeQuarters />
      );
    // this is used A LOT of areas, so hard coding the style here so that it's easier to update everywhere
    return (
      <span
        style={{
          alignItems: 'center',
          display: 'inline-flex',
          flexDirection: 'row',
          fontSize: '10px',
          gap: '1em',
          justifyContent: 'center',
        }}
      >
        {icon}
        <div style={{ textAlign: 'center' }}>
          Until
          <br />
          {expires.format('MM/DD/YYYY')}
        </div>
      </span>
    );
  }

  return <></>;
}
